import * as React from 'react'

import { Box, Section } from '@factorialco/deprecated-design-system'
import type { Field } from 'factorial-form'

import { observer } from 'mobx-react'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import { LeaveTypeSelectorInput } from 'modules/core/components/Inputs/LeaveTypeSelectorInput'
import Text from 'modules/core/components/Inputs/Text'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'

import type AllowanceForm from 'modules/timeoff/lib/AllowanceForm'
import VisibleLeaveTypes from 'modules/timeoff/lib/visibleLeaveTypes'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'
import type Allowance from 'modules/timeoff/store/models/Allowance'
import { ALL_SET_LEAVE_NAMES } from 'modules/timeoff/store/models/LeaveType'

import AllowanceSourceUnitsSelect from './_AllowanceSourceUnits'

interface Props {
  allowance: Allowance
  allowanceForm: AllowanceForm
  isOvertime: boolean
  hasOvertimeTimeOff: boolean
  hasAccruedPaidTimeOff: boolean
}

const BasicInformation = ({
  allowance,
  allowanceForm,
  isOvertime,
  hasAccruedPaidTimeOff,
  hasOvertimeTimeOff,
}: Props) => {
  const visibleLeaveTypes = VisibleLeaveTypes(allowance, leaveTypes.active)

  const onSelectLeaveTypes = (leaveTypeIds: number[]) => {
    allowanceForm.setValues({
      leave_type_ids: leaveTypeIds,
    })
  }

  const overtimeLeaveType = visibleLeaveTypes.find(
    (leaveType) => leaveType.attributes.identifier === ALL_SET_LEAVE_NAMES.overtime_compensation
  )

  const leaveTypesExceptOvertime = visibleLeaveTypes.filter(
    (leaveType) => leaveType.attributes.identifier !== ALL_SET_LEAVE_NAMES.overtime_compensation
  )

  const selectedIds = allowanceForm.get('leave_type_ids').value
  const visibleLeaveTypeIds = visibleLeaveTypes.map((leaveType) => leaveType.id)
  const activeSelectedIds = selectedIds.filter((id: number) => visibleLeaveTypeIds.includes(id))

  return (
    <Section.Form
      illustration="document-edit"
      title={i18n.t('time_off.allowances.page.basic_information.title')}
      description={i18n.t('time_off.allowances.page.basic_information.description')}
      link={{
        href: helpCenterUrl('time_off_available_days'),
        label: i18n.t('settings.timeoff_policies.basic_information.allowance.link'),
      }}
      submitLabel={i18n.t('common.submit')}
    >
      <Box className="settings_timeoff_policy_counter_choose_type_of_absences" gap="s16">
        <Text
          label={i18n.t('time_off.allowances.page.labels.name')}
          field={allowanceForm.get('name')}
          mandatory
          extra={{ 'data-intercom-target': 'timeoff-allowances-name-input' }}
        />
        <AllowanceSourceUnitsSelect
          allowance={allowance}
          allowanceForm={allowanceForm}
          hasOvertimeTimeOff={hasOvertimeTimeOff}
          hasAccruedPaidTimeOff={hasAccruedPaidTimeOff}
          disabled={!allowance.isNew}
        />
        {!isOvertime ? (
          <LeaveTypeSelectorInput
            label={i18n.t('time_off.allowances.page.labels.leave_types')}
            secondaryLabel={
              <InformationTooltip
                information={i18n.t('time_off.allowances.page.information.leave_types')}
                icon="question"
              />
            }
            description={i18n.t('time_off.allowances.page.description.leave_types')}
            leaveTypes={leaveTypesExceptOvertime}
            selectedIds={activeSelectedIds}
            onSubmit={onSelectLeaveTypes}
            showCreateLeave={leaveTypesExceptOvertime.length === 0}
            mandatory
          />
        ) : (
          <Text
            label={i18n.t('time_off.allowances.page.labels.leave_types')}
            secondaryLabel={
              <InformationTooltip
                information={i18n.t('time_off.allowances.page.information.leave_types')}
                icon="question"
              />
            }
            description={i18n.t('time_off.allowances.page.description.leave_types')}
            field={
              {
                value: overtimeLeaveType?.name,
              } as Field
            }
            readonly={isOvertime}
          />
        )}
      </Box>
    </Section.Form>
  )
}

export default observer(BasicInformation)
